import { Create, Edit, SimpleForm, TextInput, SelectInput } from 'react-admin';
import { Box, Typography, Paper, Chip, Alert, Grid } from '@mui/material';
import { Business, VerifiedUser, Category } from '@mui/icons-material';
import { PROVINCE_CHOICES, ACCREDITATION_CHOICES, STATUS_CHOICES, INDUSTRY_SECTORS } from './constants';

export const TrainingProviderCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 3 }}>
        Register a new training provider. All fields marked with * are required for SETA compliance records.
      </Alert>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedUser fontSize="small" /> Provider Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="providerName" label="Provider Name (Legal Entity)" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="providerRegistrationNumber" label="CIPC Registration Number" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="setaAccreditationNumber" label="SETA Accreditation Number" fullWidth placeholder="e.g., NQA-001" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="accreditationStatus" choices={ACCREDITATION_CHOICES} fullWidth defaultValue="Pending" label="Accreditation Status" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="accreditationExpiry" label="Accreditation Expiry Date" type="date" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="status" choices={STATUS_CHOICES} fullWidth defaultValue="Pending" label="Provider Status" />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business fontSize="small" /> Contact Information
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary', fontWeight: 600 }}>
          Physical Address
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextInput source="physicalAddress.streetAddress" label="Street Address" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="physicalAddress.suburb" label="Suburb" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="physicalAddress.city" label="City" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="physicalAddress.province" choices={PROVINCE_CHOICES} fullWidth label="Province" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="physicalAddress.postalCode" label="Postal Code" fullWidth />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Category fontSize="small" /> Industry Sectors
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
          Select all sectors that apply to this provider.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {INDUSTRY_SECTORS.map(sector => (
            <Chip key={sector} label={sector} clickable sx={{ cursor: 'pointer' }} />
          ))}
        </Box>
        <TextInput source="industrySectors" label="Industry Sectors (comma-separated)" fullWidth placeholder="Mining, Manufacturing, Construction" />
      </Paper>
    </SimpleForm>
  </Create>
);

export const TrainingProviderEdit = () => (
  <Edit>
    <SimpleForm>
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedUser fontSize="small" /> Provider Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="providerName" label="Provider Name" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="providerRegistrationNumber" label="CIPC Registration Number" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="setaAccreditationNumber" label="SETA Accreditation Number" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="accreditationStatus" choices={ACCREDITATION_CHOICES} fullWidth label="Accreditation Status" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="accreditationExpiry" label="Accreditation Expiry Date" type="date" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="status" choices={STATUS_CHOICES} fullWidth label="Provider Status" />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business fontSize="small" /> Contact Information
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary', fontWeight: 600 }}>
          Physical Address
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextInput source="physicalAddress.streetAddress" label="Street Address" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="physicalAddress.suburb" label="Suburb" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="physicalAddress.city" label="City" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="physicalAddress.province" choices={PROVINCE_CHOICES} fullWidth label="Province" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="physicalAddress.postalCode" label="Postal Code" fullWidth />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Category fontSize="small" /> Industry Sectors
        </Typography>
        <TextInput source="industrySectors" label="Industry Sectors" fullWidth />
      </Paper>
    </SimpleForm>
  </Edit>
);
